/**
 * WhatsApp Call Bar — floating bar shown during an active WhatsApp call.
 * Displays call status, duration, mute/end controls.
 */

import { Phone, PhoneOff, Mic, MicOff, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWhatsAppStore } from '@/stores/whatsapp-store';
import { useModuleStore } from '@/stores/module-store';
import type { WhatsAppModule } from '@/modules/tpa/whatsapp/whatsapp.module';

export function WhatsAppCallBar() {
  const activeCall = useWhatsAppStore((s) => s.activeWhatsAppCall);
  const tpaModule = useModuleStore((s) => s.tpaModule) as WhatsAppModule | null;

  if (!activeCall) return null;

  const { status, contactNumber, duration, isMuted, direction } = activeCall;

  const isConnecting = status === 'connecting' || status === 'ringing';
  const isActive = status === 'ongoing' || status === 'accepted';
  const isEnded = status === 'terminated' || status === 'rejected' || status === 'missed';

  if (isEnded) return null;

  const handleEndCall = () => {
    tpaModule?.endCall();
  };

  const handleToggleMute = () => {
    tpaModule?.toggleCallMute();
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const statusLabel = (() => {
    switch (status) {
      case 'connecting': return 'Connecting...';
      case 'ringing': return direction === 'outbound' ? 'Ringing...' : 'Incoming call';
      case 'accepted': return 'Connected';
      case 'ongoing': return formatDuration(duration);
      default: return status;
    }
  })();

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
      <div
        className={cn(
          'flex items-center gap-3 rounded-full px-4 py-2 shadow-lg border',
          'bg-background/95 backdrop-blur-md',
          isConnecting && 'border-yellow-500/30',
          isActive && 'border-emerald-500/30',
        )}
      >
        {/* Call indicator */}
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            isConnecting && 'bg-yellow-500/15',
            isActive && 'bg-emerald-500/15',
          )}
        >
          {isConnecting ? (
            <Loader2 className="h-4 w-4 text-yellow-600 animate-spin" />
          ) : (
            <Phone className="h-4 w-4 text-emerald-600" />
          )}
        </div>

        {/* Call info */}
        <div className="flex flex-col min-w-[100px]">
          <span className="text-xs font-medium truncate max-w-[150px]">
            {contactNumber}
          </span>
          <span
            className={cn(
              'text-[10px]',
              isConnecting && 'text-yellow-600',
              isActive && 'text-emerald-600',
            )}
          >
            {statusLabel}
          </span>
        </div>

        {/* WhatsApp badge */}
        <span className="text-[10px] font-medium text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded-full">
          WA
        </span>

        {/* Mute button (only when active) */}
        {isActive && (
          <button
            onClick={handleToggleMute}
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
              isMuted
                ? 'bg-red-500/15 text-red-600 hover:bg-red-500/25'
                : 'bg-muted text-foreground hover:bg-muted/80'
            )}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          </button>
        )}

        {/* End call button */}
        <button
          onClick={handleEndCall}
          className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
          title="End call"
        >
          <PhoneOff className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
